import { useMemo, useState } from "react";
import { motion } from "framer-motion";
import { CalendarCheck, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";

const TIME_SLOTS = ["09:00", "10:00", "11:00", "12:00", "13:00", "14:00", "15:00", "16:00", "17:00"];

export default function AppointmentPicker({ referenceCode }) {
  const [date, setDate] = useState("");
  const [time, setTime] = useState("");
  const [loading, setLoading] = useState(false);
  const [saved, setSaved] = useState(null);

  const minDate = useMemo(() => {
    const d = new Date();
    d.setDate(d.getDate() + 1);
    return d.toISOString().slice(0, 10);
  }, []);

  const save = async () => {
    if (!date || !time) {
      toast.error("Lütfen tarih ve saat seçin.");
      return;
    }
    setLoading(true);
    try {
      await api.post(`/submissions/appointment/${referenceCode}`, { date, time });
      setSaved({ date, time });
      toast.success("Randevu talebiniz alındı.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  if (saved) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        className="mt-6 rounded-2xl border border-[#0D9488]/30 bg-[#0D9488]/5 p-5"
        data-testid="appointment-saved"
      >
        <p className="text-xs font-semibold uppercase tracking-widest text-[#0D9488]">Randevu Talebiniz Alındı</p>
        <p className="font-display text-lg font-bold text-[#0F172A] mt-1.5">
          {new Date(`${saved.date}T12:00:00`).toLocaleDateString("tr-TR", { day: "numeric", month: "long", year: "numeric", weekday: "long" })} — {saved.time}
        </p>
        <p className="text-sm text-[#475569] mt-1.5">Danışmanlarımız randevu onayı için sizi telefonla arayacak.</p>
      </motion.div>
    );
  }

  return (
    <div className="mt-6 rounded-2xl border border-[#E2E8F0] bg-[#F8FAFC] p-5 text-left" data-testid="appointment-picker">
      <p className="text-xs font-semibold uppercase tracking-widest text-[#0000FF] flex items-center gap-2">
        <CalendarCheck size={14} />
        Görüşme Randevusu Planlayın (Opsiyonel)
      </p>
      <div className="mt-4">
        <label className="block text-xs font-semibold text-[#475569] mb-1.5">Tarih</label>
        <input
          type="date"
          min={minDate}
          value={date}
          onChange={(e) => setDate(e.target.value)}
          className="w-full rounded-xl border border-[#E2E8F0] bg-white px-4 py-2.5 text-sm text-[#0F172A] outline-none transition-all focus:border-[#0000FF] focus:ring-4 focus:ring-[#0000FF]/10"
          data-testid="appointment-date-input"
        />
      </div>
      <div className="mt-4">
        <label className="block text-xs font-semibold text-[#475569] mb-1.5">Saat (Mesai saatleri)</label>
        <div className="flex flex-wrap gap-1.5" data-testid="appointment-time-slots">
          {TIME_SLOTS.map((slot) => (
            <button
              key={slot}
              type="button"
              onClick={() => setTime(slot)}
              data-testid={`time-slot-${slot.replace(":", "-")}`}
              className={`rounded-lg border px-3 py-1.5 font-mono text-xs font-semibold transition-all active:scale-[0.97] ${
                time === slot
                  ? "border-[#0000FF] bg-[#0000FF] text-white"
                  : "border-[#E2E8F0] bg-white text-[#475569] hover:border-[#93C5FD]"
              }`}
            >
              {slot}
            </button>
          ))}
        </div>
      </div>
      <button
        type="button"
        onClick={save}
        disabled={loading}
        data-testid="appointment-save-button"
        className="mt-4 w-full inline-flex items-center justify-center gap-2 rounded-full bg-[#0F172A] text-white text-sm font-semibold px-5 py-2.5 transition-colors hover:bg-[#0000FF] disabled:opacity-60"
      >
        {loading ? <Loader2 size={15} className="animate-spin" /> : <CalendarCheck size={15} />}
        Randevu Talebini Gönder
      </button>
    </div>
  );
}
